import { ApiSet, BaseResponse, IndexApiSet, useDeleteApi, useIndexApi, usePostApi, usePutApi } from 'utils/network/api_hooks'
import { Form, useEffectSkipFirst } from 'utils/hooks'
import { CraifHttpClient } from '../utils/network/axios'
import { PagingResponse } from 'entities'
import { Material, MaterialForm, MaterialSearchForm } from 'entities/material'

type MaterialsResponse = PagingResponse & {
    results: Material[]
}

export function useFetchMaterialsApi(searchForm?: Form<MaterialSearchForm>): IndexApiSet<MaterialsResponse> & { execute: () => void } {
    const apiPath = 'materials/'
    const api = useIndexApi<MaterialsResponse>(new CraifHttpClient(), {
        initialResponse: { count: 0, results: [] },
        initialState: { page: searchForm?.object?.page || 1 },
    })

    const execute = (): void => {
        api.execute(apiPath, { params: searchForm?.object })
    }

    useEffectSkipFirst(() => {
        searchForm?.update((f) => {
            if (api.pageSet.page) f.page = api.pageSet.page
            if (api.pageSet.perPage) f.perPage = api.pageSet.perPage
        })
    }, [api.pageSet.page, api.pageSet.perPage])

    useEffectSkipFirst(() => {
        execute()
    }, [searchForm?.object])

    return { ...api, execute: execute }
}

export function usePostMaterialApi(): ApiSet<BaseResponse> & { execute: (form: Form<MaterialForm>) => void } {
    const apiSet = usePostApi<BaseResponse, MaterialForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<MaterialForm>) => {
        const apiPath = `materials/`
        apiSet.execute(apiPath, form)
    }

    return { ...apiSet, execute: execute }
}

export function usePutMaterialApi(): ApiSet<BaseResponse> & { execute: (form: Form<MaterialForm>) => void } {
    const apiSet = usePutApi<BaseResponse, MaterialForm>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (form: Form<MaterialForm>) => {
        const apiPath = `materials/${form.object.id}/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useBulkPutMaterialsApi(): ApiSet<BaseResponse> & { execute: (form: Form<MaterialForm[]>) => void } {
    const apiSet = usePutApi<BaseResponse, MaterialForm[]>(
        new CraifHttpClient(),
        {
            initialResponse: {},
        },
        { formatJson: true }
    )

    const execute = (form: Form<MaterialForm[]>) => {
        const apiPath = `materials/bulk_update/`
        apiSet.execute(apiPath, form.object)
    }

    return { ...apiSet, execute: execute }
}

export function useDeleteMaterialApi(): ApiSet<BaseResponse> & {
    execute: (id: number) => void
} {
    const api = useDeleteApi<BaseResponse>(new CraifHttpClient(), {
        initialResponse: {},
    })

    const execute = (id: number): void => {
        const apiPath = `materials/${id}/`
        api.execute(apiPath)
    }

    return { ...api, execute: execute }
}
